// ===================================================================
// File: src/components/Receipts/Receipts.jsx
// Description: Camera/Upload OCR + per-receipt details (synced)
// - Persists to StorageContext file: receipts/mudoReceipts.json
// - StorageContext auto-virtualises into receipts/items/*.json + index.json
// - Cross-device polling via loadJSONFresh (Drive mode)
// - Emits "mudo:receipts-updated" on changes
// ===================================================================

import React, { useEffect, useMemo, useRef, useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Tesseract from "tesseract.js";
import { MaterialContext } from "../../contexts/MaterialContext";
import { useStorage } from "../../contexts/StorageContext";
import BackButton from "../Common/BackButton";

const FS_PATH = "receipts/mudoReceipts.json";
const LS_DRAFT_PO = "mudoDraftPO";

const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmtMoney = (n) => {
  const x = Number(n || 0);
  if (!isFinite(x)) return "—";
  return x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const money2 = (n) => (Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getUnitCost = (m) => {
  const purchaseCost = toNum(m?.purchaseCost);
  const conv = Math.max(1, toNum(m?.conversionAmount) || 1);
  return conv > 0 ? purchaseCost / conv : 0;
};
const getUnit = (m) => m?.conversionUnit || m?.trackingUnit || m?.unit || "unit";
const getSupplierFrom = (m, it) =>
  (it?.supplier ??
    m?.supplier ??
    m?.supplierName ??
    m?.vendor ??
    m?.brand ??
    m?.maker ??
    m?.manufacturer ??
    "") || "";
const normalizeUrl = (u) => {
  if (!u) return "";
  let s = String(u).trim();
  if (!s) return "";
  if (typeof u === "object" && u.url) s = String(u.url).trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("www.")) return `https://${s}`;
  if (/^[a-z0-9.-]+\.[a-z]{2,}([/:].*)?$/i.test(s)) return `https://${s}`;
  return s;
};
const getWebsiteFrom = (m, it) => {
  const candidates = [
    it?.website, it?.supplierLink, it?.url, it?.href, it?.link,
    m?.supplierLink, m?.website, m?.Website, m?.webSite, m?.productUrl, m?.productURL,
    m?.supplierUrl, m?.supplierURL, m?.supplierWebsite, m?.websiteUrl, m?.websiteURL,
    m?.url, m?.href, m?.link, m?.web, m?.page, m?.homepage, m?.buyUrl, m?.shopUrl,
    m?.storeUrl, m?.sourceUrl, m?.vendorUrl, m?.manufacturerUrl,
    m?.supplier?.url, m?.supplier?.website, m?.supplier?.webSite,
    m?.vendor?.url, m?.vendor?.website,
    m?.manufacturer?.url, m?.manufacturer?.website,
    m?.source?.url,
  ];
  const first = candidates.find(
    (v) => (typeof v === "string" && v.trim()) || (v && typeof v === "object" && v.url)
  );
  return normalizeUrl(first || "");
};

function nowIso() { return new Date().toISOString(); }
function normalizeDate(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  let m = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
  if (m) {
    const y = m[1], mo = m[2], d = m[3];
    return `${y}-${String(mo).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  }
  m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (m) {
    let y = m[3];
    if (y.length === 2) y = `20${y}`;
    const dFirst = Number(m[1]) > 12;
    const d = dFirst ? m[1] : m[2];
    const mo = dFirst ? m[2] : m[1];
    return `${y}-${String(mo).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  }
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const P = (n) => String(n).padStart(2,"0");
    return `${d.getFullYear()}-${P(d.getMonth()+1)}-${P(d.getDate())}`;
  }
  return s;
}
function deepEqual(a, b) { try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; } }

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(new Error("read-failed"));
    fr.onload = () => resolve(String(fr.result || ""));
    fr.readAsDataURL(file);
  });
}

function parseOcrText(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  let total = null;
  const moneyRegex = /(?:total|amount\s+due|grand\s+total|balance)\s*[:\-]?\s*([$£€]?\s*\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{2})?)/i;
  for (const l of lines) {
    const m = l.match(moneyRegex);
    if (m) {
      total = Number(
        String(m[1])
          .replace(/[^\d.,]/g, "")
          .replace(/,/g, "")
          .replace(/(\d+)\.(\d{3})$/, "$1$2")
          .replace(/(\d+),(?=\d{2}$)/, "$1.")
      );
      if (isFinite(total)) break;
    }
  }
  if (!isFinite(total)) {
    const nums = [...text.matchAll(/([$£€]?\s*\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{2})?)/g)]
      .map((m) => Number(String(m[0]).replace(/[^\d.,]/g, "").replace(/,/g, "")))
      .filter((n) => isFinite(n));
    if (nums.length) total = Math.max(...nums);
  }

  let date = null;
  const dateRegex = /\b((?:\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})|(?:\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}))\b/;
  const md = text.match(dateRegex);
  if (md) date = normalizeDate(md[1]);

  const bad = /(invoice|receipt|tax|abn|gst|vat|quote|bill|amount|total|subtotal|balance)/i;
  let supplier = null;
  for (let i = 0; i < Math.min(10, lines.length); i++) {
    const L = lines[i];
    if (L.length >= 3 && /[A-Za-z]/.test(L) && !bad.test(L)) {
      supplier = L.replace(/[^A-Za-z0-9 \-_.&]/g, "").trim();
      if (supplier) break;
    }
  }

  const skuCandidates = Array.from(
    new Set(
      [...text.matchAll(/\b([A-Z0-9][A-Z0-9\-_]{3,})\b/gi)]
        .map((m) => m[1])
        .filter((s) => /\d/.test(s) && !/TOTAL|SUB|TAX|GST|VAT/i.test(s))
        .slice(0, 8)
    )
  );

  return { total: isFinite(total) ? total : null, date, supplier, skuCandidates };
}

function buildDraftPO(active, materialsIndex) {
  if (!active) return null;

  const matched = new Map();
  if (active.meta?.materialId) {
    const m = materialsIndex.byId.get(String(active.meta.materialId));
    if (m) matched.set(String(m.id), m);
  }
  if (active.parsed?.skuCandidates?.length) {
    for (const tok of active.parsed.skuCandidates) {
      const hit = materialsIndex.bySku.get(tok.toUpperCase());
      if (hit) matched.set(String(hit.id), hit);
    }
  }

  let supplierName = "";
  if (matched.size) {
    const m0 = [...matched.values()][0];
    supplierName = getSupplierFrom(m0, null);
  }
  if (!supplierName) supplierName = active.parsed?.supplier || "";

  let poItems = [];
  if (matched.size) {
    poItems = [...matched.values()].map((m) => {
      const unit = getUnit(m);
      const unitCost = getUnitCost(m);
      return {
        materialId: m.id,
        name: m.name || "Unnamed",
        sku: m.sku || "",
        supplier: getSupplierFrom(m, null) || supplierName || "",
        website: getWebsiteFrom(m, null),
        image: m.image || m.photo || null,
        unit,
        unitCost,
        qty: 1,
        category: m.category || "",
        notes: `From receipt ${active.name}`,
      };
    });
  } else {
    poItems = [
      {
        materialId: "",
        name: active.parsed?.supplier ? `${active.parsed.supplier} purchase` : "Receipt item",
        sku: "",
        supplier: supplierName || "",
        website: "",
        image: null,
        unit: "unit",
        unitCost: Number(active.meta?.total || active.parsed?.total || 0),
        qty: 1,
        category: "Misc",
        notes: `From receipt ${active.name}`,
      },
    ];
  }

  const subtotal = poItems.reduce((s, it) => s + Number(it.unitCost || 0) * Number(it.qty || 0), 0);
  const tax = 0;
  const total = subtotal + tax;

  return { supplierName, items: poItems, summary: { subtotal, tax, total } };
}

export default function Receipts() {
  const navigate = useNavigate();
  const { materials = [] } = useContext(MaterialContext);
  const { loadJSON, loadJSONFresh, saveJSON, ensureDir, hasWorkspace, isDriveLinked } = useStorage();

  const [items, setItems] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [query, setQuery] = useState("");
  const [filterMaterial, setFilterMaterial] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const fileInputRef = useRef(null);

  const itemsRef = useRef(items);
  const drivePollJitterMsRef = useRef(Math.floor(Math.random() * 20000));
  useEffect(() => { itemsRef.current = items; }, [items]);

  const hydrate = useCallback(async () => {
    try {
      const disk = await loadJSON(FS_PATH, []);
      if (Array.isArray(disk)) setItems(disk);
    } catch {}
  }, [loadJSON]);

  useEffect(() => { hydrate(); }, [hydrate]);

  useEffect(() => {
    (async () => {
      try {
        await ensureDir("receipts");
        await saveJSON(FS_PATH, items);
        window.dispatchEvent(new Event("mudo:receipts-updated"));
      } catch {}
    })();
  }, [items, ensureDir, saveJSON]);

  useEffect(() => {
    const onRestore = () => hydrate();
    window.addEventListener("mudo:vault-restored", onRestore);
    window.addEventListener("mudo:files-restored", onRestore);
    return () => {
      window.removeEventListener("mudo:vault-restored", onRestore);
      window.removeEventListener("mudo:files-restored", onRestore);
    };
  }, [hydrate]);

  useEffect(() => {
    let t = null;
    const BASE_POLL_MS = 15000;
    const POLL_MS = BASE_POLL_MS + drivePollJitterMsRef.current;
    async function tick() {
      try {
        if (!hasWorkspace || !isDriveLinked || !loadJSONFresh) return;
        const remote = await loadJSONFresh(FS_PATH, []);
        if (!deepEqual(remote, itemsRef.current)) {
          setItems(Array.isArray(remote) ? remote : []);
          window.dispatchEvent(new Event("mudo:receipts-updated"));
        }
      } finally {
        t = setTimeout(tick, POLL_MS);
      }
    }
    if (isDriveLinked) t = setTimeout(tick, POLL_MS);
    return () => t && clearTimeout(t);
  }, [hasWorkspace, isDriveLinked, loadJSONFresh]);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraOn, setCameraOn] = useState(false);
  useEffect(() => {
    let stream;
    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } } });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch {}
    };
    if (cameraOn) start();
    return () => { if (stream) stream.getTracks().forEach((t) => t.stop()); };
  }, [cameraOn]);

  const materialsIndex = useMemo(() => {
    const bySku = new Map();
    const bySupplier = new Map();
    const byId = new Map();
    for (const m of materials) {
      if (m.sku) bySku.set(String(m.sku).toUpperCase(), m);
      const sup = (m.supplierName || m.supplier || "").trim().toUpperCase();
      if (sup) {
        const arr = bySupplier.get(sup) || [];
        arr.push(m);
        bySupplier.set(sup, arr);
      }
      if (m.id) byId.set(String(m.id), m);
    }
    return { bySku, bySupplier, byId };
  }, [materials]);

  const runOcr = async (receiptId, fileOrDataUrl) => {
    if (typeof fileOrDataUrl !== "string") {
      if (/pdf$/i.test(fileOrDataUrl.type) || /\.pdf$/i.test(fileOrDataUrl.name)) {
        setItems((prev) => prev.map((r) => (r.id === receiptId ? { ...r, ocrStatus: "done", ocrProgress: 100, ocrText: "", parsed: null } : r)));
        return;
      }
    } else if (/^data:application\/pdf/i.test(fileOrDataUrl)) {
      setItems((prev) => prev.map((r) => (r.id === receiptId ? { ...r, ocrStatus: "done", ocrProgress: 100, ocrText: "", parsed: null } : r)));
      return;
    }

    let fileForOCR = null;
    if (typeof fileOrDataUrl === "string") {
      const res = await fetch(fileOrDataUrl);
      fileForOCR = await res.blob();
    } else {
      fileForOCR = fileOrDataUrl;
    }

    setItems((prev) => prev.map((r) => (r.id === receiptId ? { ...r, ocrStatus: "running", ocrProgress: 0 } : r)));

    try {
      const { data } = await Tesseract.recognize(fileForOCR, "eng", {
        logger: (m) => {
          if (m.status === "recognizing text" && m.progress != null) {
            setItems((prev) => prev.map((r) => (r.id === receiptId ? { ...r, ocrProgress: Math.round(m.progress * 100) } : r)));
          }
        },
      });

      const parsed0 = parseOcrText(data.text || "");
      const parsed = { ...parsed0 };

      let suggestedMaterialId = "";
      if (parsed.supplier) {
        const sup = parsed.supplier.trim().toUpperCase();
        const candidates = materialsIndex.bySupplier.get(sup);
        if (candidates?.length) suggestedMaterialId = String(candidates[0].id);
      }
      if (!suggestedMaterialId && parsed.skuCandidates?.length) {
        for (const tok of parsed.skuCandidates) {
          const hit = materialsIndex.bySku.get(tok.toUpperCase());
          if (hit) { suggestedMaterialId = String(hit.id); break; }
        }
      }
      parsed.suggestedMaterialId = suggestedMaterialId;

      setItems((prev) => prev.map((r) => r.id === receiptId ? { ...r, ocrStatus: "done", ocrProgress: 100, ocrText: data.text || "", parsed } : r));
    } catch {
      setItems((prev) => prev.map((r) => r.id === receiptId ? { ...r, ocrStatus: "error", ocrProgress: 0 } : r));
    }
  };

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(
      (f) => /^image\//.test(f.type) || /pdf$/i.test(f.type) || /\.pdf$/i.test(f.name)
    );
    if (!files.length) return;

    const now = Date.now();
    const newItems = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const dataUrl = await fileToDataURL(f);
      newItems.push({
        id: `${now}-${Math.random().toString(36).slice(2, 7)}`,
        name: f.name,
        mime: f.type || (/\.(pdf)$/i.test(f.name) ? "application/pdf" : ""),
        size: f.size ?? undefined,
        addedAt: Date.now(),
        ocrStatus: "queued",
        ocrProgress: 0,
        ocrText: "",
        parsed: null,
        meta: { note: "", materialId: "", date: "", total: "" },
        dataUrl,
      });
    }

    setItems((prev) => [...newItems, ...prev]);

    (async () => {
      for (let i = 0; i < newItems.length; i++) {
        await runOcr(newItems[i].id, files[i]);
      }
    })();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const captureFromCamera = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0);
    const dataUrl = c.toDataURL("image/png");
    const f = { name: `camera-${Date.now()}.png`, type: "image/png" };
    const newItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: f.name,
      mime: f.type,
      size: undefined,
      addedAt: Date.now(),
      ocrStatus: "queued",
      ocrProgress: 0,
      ocrText: "",
      parsed: null,
      meta: { note: "", materialId: "", date: "", total: "" },
      dataUrl,
    };
    setItems((prev) => [newItem, ...prev]);
    runOcr(newItem.id, dataUrl);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((r) => {
      const hitQ =
        !q ||
        (r.name || "").toLowerCase().includes(q) ||
        (r.parsed?.supplier || "").toLowerCase().includes(q) ||
        (r.meta?.note || "").toLowerCase().includes(q) ||
        (r.ocrText || "").toLowerCase().includes(q);
      const hitMat = !filterMaterial || String(r.meta?.materialId || "") === String(filterMaterial);
      return hitQ && hitMat;
    });
  }, [items, query, filterMaterial]);

  const active = items.find((r) => r.id === activeId);

  const acceptSuggestions = (r) => {
    const p = r.parsed || {};
    setItems((prev) =>
      prev.map((x) =>
        x.id === r.id
          ? {
              ...x,
              meta: {
                ...x.meta,
                total: p.total ?? x.meta.total,
                date: normalizeDate(p.date || x.meta.date) || x.meta.date,
                materialId: p.suggestedMaterialId || x.meta.materialId,
              },
            }
          : x
      )
    );
  };

  const deleteReceipt = (id) => {
    if (!confirm("Delete this receipt?")) return;
    setItems((prev) => prev.filter((r) => r.id !== id));
    if (activeId === id) setActiveId(null);
  };

  const createPOFromReceipt = (r) => {
    const matched = new Map();

    if (r.meta?.materialId) {
      const m = materials.find((mm) => String(mm.id) === String(r.meta.materialId));
      if (m) matched.set(String(m.id), m);
    }
    if (r.parsed?.skuCandidates?.length) {
      for (const tok of r.parsed.skuCandidates) {
        const hit = materials.find((mm) => String(mm.sku || "").toUpperCase() === tok.toUpperCase());
        if (hit) matched.set(String(hit.id), hit);
      }
    }

    let supplierName = "";
    if (matched.size) {
      const m0 = [...matched.values()][0];
      supplierName = getSupplierFrom(m0, null);
    }
    if (!supplierName) supplierName = r.parsed?.supplier || "";

    let poItems = [];
    if (matched.size) {
      poItems = [...matched.values()].map((m) => {
        const unit = getUnit(m);
        const unitCost = getUnitCost(m);
        return {
          materialId: m.id,
          name: m.name || "Unnamed",
          sku: m.sku || "",
          supplier: getSupplierFrom(m, null) || supplierName || "",
          website: getWebsiteFrom(m, null),
          image: m.image || m.photo || null,
          unit,
          unitCost,
          qty: 1,
          category: m.category || "",
          notes: `From receipt ${r.name}`,
        };
      });
    } else {
      poItems = [
        {
          materialId: "",
          name: r.parsed?.supplier ? `${r.parsed.supplier} purchase` : "Receipt item",
          sku: "",
          supplier: supplierName || "",
          website: "",
          image: null,
          unit: "unit",
          unitCost: Number(r.meta?.total || r.parsed?.total || 0),
          qty: 1,
          category: "Misc",
          notes: `From receipt ${r.name}`,
        },
      ];
    }

    const draft = {
      id: `draft-${Date.now()}`,
      status: "Draft",
      createdAt: Date.now(),
      supplier: supplierName || "",
      receiptId: r.id,
      note: r.meta?.note || "",
      items: poItems,
    };

    try { localStorage.setItem(LS_DRAFT_PO, JSON.stringify(draft)); } catch {}
    navigate("/purchase-orders");
  };

  return (
    <div className="min-h-screen bg-neutral-900 text-white p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Receipts</h1>
        <div className="flex gap-2 items-center">
          <BackButton to="/dashboard" label="Back to Dashboard" />
          <button
            className="px-3 py-1 rounded bg-zinc-800 border border-white/10"
            onClick={() => navigate("/receipts-log")}
            title="Open the synced Receipts log"
          >
            View Log
          </button>
          <button
            className="px-3 py-1 rounded bg-zinc-800 border border-white/10"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload
          </button>
          <button
            className="px-3 py-1 rounded bg-zinc-800 border border-white/10"
            onClick={() => setCameraOn((v) => !v)}
          >
            {cameraOn ? "Close Camera" : "Camera"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`flex items-center justify-center h-[68px] w-[320px] rounded border-2 border-dashed transition ${
            dragOver ? "border-emerald-400 bg-emerald-400/10" : "border-white/15"
          }`}
        >
          <div className="text-xs opacity-80 text-center">
            Drag and drop images or PDFs here
            <br />
            or use the Upload or Camera buttons
          </div>
        </div>

        <input
          placeholder="Search name, note, text, or material…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="px-3 py-2 rounded bg-zinc-800 border border-white/10 w-[340px]"
        />
        <button className="px-2 py-1 rounded bg-zinc-700" onClick={() => setQuery("")}>
          Clear
        </button>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs opacity-60">Filter by material:</span>
          <select
            value={filterMaterial}
            onChange={(e) => setFilterMaterial(e.target.value)}
            className="px-2 py-2 rounded bg-zinc-800 border border-white/10"
          >
            <option value="">All materials</option>
            {materials.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
      </div>

      {cameraOn && (
        <div className="flex items-center gap-3 bg-zinc-800/50 border border-white/10 p-3 rounded">
          <video ref={videoRef} className="h-40 rounded bg-black" />
          <canvas ref={canvasRef} className="hidden" />
          <button className="px-3 py-2 rounded bg-emerald-500 text-black" onClick={captureFromCamera}>
            Capture
          </button>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        accept="image/*,.pdf"
        multiple
        className="hidden"
        onChange={(e) => addFiles(e.target.files)}
      />

      {filtered.length === 0 ? (
        <p className="italic opacity-70">No receipts found.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((r) => (
            <div key={r.id} className="rounded-lg overflow-hidden bg-zinc-900 border border-white/10 shadow relative">
              <div className="flex items-center justify-between p-2 text-xs border-b border-white/10">
                <div className="truncate">{r.name}</div>
                <div className="flex items-center gap-2">
                  {r.ocrStatus === "running" && <span className="text-amber-300">OCR {r.ocrProgress}%</span>}
                  {r.ocrStatus === "error" && <span className="text-red-300">OCR failed</span>}
                  <button className="px-2 py-1 bg-zinc-800 rounded" onClick={() => setActiveId(r.id)}>
                    Details
                  </button>
                  <button className="px-2 py-1 bg-red-600 rounded" onClick={() => deleteReceipt(r.id)}>
                    Delete
                  </button>
                </div>
              </div>
              <div className="bg-black/40 h-56 flex items-center justify-center">
                {/^data:application\/pdf/i.test(r.dataUrl || "") || /\bpdf\b/i.test(r.mime || "") ? (
                  <div className="text-xs opacity-70 p-4 text-center">
                    PDF uploaded
                    <br />
                    preview not rendered
                  </div>
                ) : r.dataUrl ? (
                  <img src={r.dataUrl} alt="" className="max-h-56 object-contain" />
                ) : (
                  <div className="text-xs opacity-70 p-4 text-center">No image</div>
                )}
              </div>
              <div className="p-3 text-sm grid grid-cols-2 gap-2 border-t border-white/10">
                <div className="opacity-70">Total</div>
                <div className="text-right font-semibold">
                  {r.meta.total ? `$${fmtMoney(r.meta.total)}`
                    : r.parsed?.total ? `$${fmtMoney(r.parsed.total)}`
                    : "—"}
                </div>
                <div className="opacity-70">Date</div>
                <div className="text-right">{r.meta.date || r.parsed?.date || "—"}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 w-full max-w-3xl rounded-lg border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between p-3 border-b border-white/10">
              <div className="font-semibold">Receipt details</div>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 rounded bg-emerald-500 text-black"
                  onClick={() => createPOFromReceipt(active)}
                >
                  Create PO from receipt
                </button>
                <button className="px-2 py-1 bg-zinc-800 rounded" onClick={() => setActiveId(null)}>
                  Close
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="p-3 border-r border-white/10">
                {/^data:application\/pdf/i.test(active.dataUrl || "") || /\bpdf\b/i.test(active.mime || "") ? (
                  <div className="h-[360px] flex items-center justify-center bg-black/40 rounded">
                    <div className="text-xs opacity-70 p-4 text-center">
                      PDF uploaded
                      <br />
                      preview not rendered
                    </div>
                  </div>
                ) : active.dataUrl ? (
                  <img src={active.dataUrl} alt="" className="max-h-[360px] object-contain mx-auto" />
                ) : (
                  <div className="h-[360px] flex items-center justify-center bg-black/40 rounded text-xs opacity-70">
                    No image
                  </div>
                )}

                <div className="mt-3 text-xs">
                  {active.ocrStatus === "running" ? (
                    <div className="text-amber-300">OCR {active.ocrProgress}%</div>
                  ) : active.ocrStatus === "done" ? (
                    <div className="opacity-60">OCR complete</div>
                  ) : active.ocrStatus === "error" ? (
                    <div className="text-red-300">OCR failed</div>
                  ) : null}
                </div>
              </div>

              <div className="p-4 space-y-3">
                <div className="rounded bg-zinc-800/60 p-3 border border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">PO Preview</div>
                    <button
                      className="text-xs px-2 py-1 rounded bg-zinc-700"
                      onClick={() => setShowPreview((v) => !v)}
                    >
                      {showPreview ? "Hide" : "Show"}
                    </button>
                  </div>

                  {showPreview && (() => {
                    const draft = buildDraftPO(active, materialsIndex);
                    if (!draft) return <div className="mt-2 text-sm opacity-70">No preview.</div>;
                    return (
                      <div className="mt-2 text-sm">
                        <div className="mb-2 opacity-80">
                          Supplier: <span className="opacity-100">{draft.supplierName || "—"}</span>
                        </div>
                        <div className="overflow-auto border border-white/10 rounded">
                          <table className="w-full text-xs">
                            <thead className="bg-white/5">
                              <tr>
                                <th className="px-2 py-2 text-left">Item</th>
                                <th className="px-2 py-2 text-left">SKU</th>
                                <th className="px-2 py-2 text-right">Qty</th>
                                <th className="px-2 py-2 text-right">Unit</th>
                                <th className="px-2 py-2 text-right">Unit Cost</th>
                                <th className="px-2 py-2 text-right">Line Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {draft.items.map((it, i) => (
                                <tr key={i} className="border-t border-white/10">
                                  <td className="px-2 py-1">{it.name}</td>
                                  <td className="px-2 py-1">{it.sku || "—"}</td>
                                  <td className="px-2 py-1 text-right">{Number(it.qty || 0)}</td>
                                  <td className="px-2 py-1 text-right">{it.unit || "unit"}</td>
                                  <td className="px-2 py-1 text-right">${money2(it.unitCost)}</td>
                                  <td className="px-2 py-1 text-right">${money2((it.unitCost || 0) * (it.qty || 0))}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="mt-2 text-right space-y-1">
                          <div>Subtotal: <strong>${money2(draft.summary.subtotal)}</strong></div>
                          <div>Total: <strong>${money2(draft.summary.total)}</strong></div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="rounded bg-zinc-800/60 p-3 border border-white/10">
                  <div className="text-sm font-semibold mb-2">Suggestions</div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="opacity-70">Supplier</div>
                    <div className="col-span-2">{active.parsed?.supplier || "—"}</div>

                    <div className="opacity-70">Date</div>
                    <div className="col-span-2">{active.parsed?.date || "—"}</div>

                    <div className="opacity-70">Total</div>
                    <div className="col-span-2">
                      {active.parsed?.total != null ? `$${fmtMoney(active.parsed.total)}` : "—"}
                    </div>

                    <div className="opacity-70">SKU hits</div>
                    <div className="col-span-2">
                      {active.parsed?.skuCandidates?.length ? active.parsed.skuCandidates.join(", ") : "—"}
                    </div>

                    <div className="opacity-70">Material match</div>
                    <div className="col-span-2">
                      {active.parsed?.suggestedMaterialId
                        ? materials.find((m) => String(m.id) === String(active.parsed.suggestedMaterialId))?.name ||
                          "Suggested"
                        : "—"}
                    </div>
                  </div>

                  <div className="mt-2">
                    <button
                      className="px-3 py-1 rounded bg-emerald-500 text-black"
                      onClick={() => acceptSuggestions(active)}
                    >
                      Accept suggestions
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 items-center">
                  <label className="text-sm opacity-70">Linked material</label>
                  <select
                    className="col-span-2 px-2 py-2 rounded bg-zinc-800 border border-white/10"
                    value={active.meta.materialId || ""}
                    onChange={(e) =>
                      setItems((prev) =>
                        prev.map((r) =>
                          r.id === active.id ? { ...r, meta: { ...r.meta, materialId: e.target.value } } : r
                        )
                      )
                    }
                  >
                    <option value="">none</option>
                    {materials.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>

                  <label className="text-sm opacity-70">Total</label>
                  <input
                    type="number"
                    step="0.01"
                    className="col-span-2 px-2 py-2 rounded bg-zinc-800 border border-white/10"
                    value={active.meta.total ?? ""}
                    onChange={(e) =>
                      setItems((prev) =>
                        prev.map((r) =>
                          r.id === active.id ? { ...r, meta: { ...r.meta, total: Number(e.target.value) } } : r
                        )
                      )
                    }
                  />

                  <label className="text-sm opacity-70">Date</label>
                  <input
                    type="text"
                    placeholder="YYYY-MM-DD"
                    className="col-span-2 px-2 py-2 rounded bg-zinc-800 border border-white/10"
                    value={active.meta.date ?? ""}
                    onChange={(e) =>
                      setItems((prev) =>
                        prev.map((r) =>
                          r.id === active.id ? { ...r, meta: { ...r.meta, date: normalizeDate(e.target.value) } } : r
                        )
                      )
                    }
                  />

                  <label className="text-sm opacity-70">Note</label>
                  <input
                    type="text"
                    className="col-span-2 px-2 py-2 rounded bg-zinc-800 border border-white/10"
                    value={active.meta.note ?? ""}
                    onChange={(e) =>
                      setItems((prev) =>
                        prev.map((r) =>
                          r.id === active.id ? { ...r, meta: { ...r.meta, note: e.target.value } } : r
                        )
                      )
                    }
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    className="px-3 py-1 rounded bg-emerald-500 text-black"
                    onClick={() => createPOFromReceipt(active)}
                  >
                    Create PO from receipt
                  </button>
                  <button className="px-3 py-1 rounded bg-zinc-700" onClick={() => setActiveId(null)}>
                    Done
                  </button>
                </div>

                <details className="mt-2">
                  <summary className="cursor-pointer opacity-70">Show OCR text</summary>
                  <pre className="mt-2 text-xs whitespace-pre-wrap bg-zinc-800/60 p-2 rounded border border-white/10 max-h-56 overflow-auto">
                    {active.ocrText || "—"}
                  </pre>
                </details>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
